/** Bounded identifier-only diagnostics; kept separate from merchant copy and rejected payloads. */
import { reportClientDiagnostic, type ClientDiagnosticInput } from './clientDiagnostics'
import type { AttemptRecord } from './insightsAttempt'
import type { ShopifyTokenSource } from './shopifyApiFetch'
import { retainInsightsEvidence, updateInsightsDelivery } from './insightsEvidence'
import type { InsightsFailureClass, RenderIssue } from './insightsFailure'
export function insightsDiagnostic(
  input: {
    failureClass: InsightsFailureClass
    phase: string
    attemptId?: string
    elapsedMs?: number
    status?: number
    issueCode?: string
    issuePath?: string
    contentType?: string
    requestId?: string
  },
  build = import.meta.env.VITE_CLIENT_BUILD ?? 'development',
): ClientDiagnosticInput {
  const paths = [`class:${input.failureClass}`, `phase:${input.phase}`, `client_build:${build}`]
  if (input.issueCode) paths.push(`issue_code:${input.issueCode}`)
  if (input.issuePath) paths.push(`issue_path:${input.issuePath}`)
  if (input.attemptId) paths.push(`attempt:${input.attemptId}`)
  if (input.elapsedMs !== undefined) paths.push(`elapsed_ms:${input.elapsedMs}`)
  if (input.contentType) paths.push(`content_type:${input.contentType}`)
  if (input.requestId) paths.push(`request_id:${input.requestId}`)
  return { schema: 'insights-load', paths, code: input.failureClass, status: input.status }
}
export function renderIssuesDiagnostic(
  issues: RenderIssue[],
  metadata: {
    attemptId?: string
    elapsedMs?: number
    status?: number
    contentType?: string
    requestId?: string
  } = {},
): ClientDiagnosticInput | undefined {
  const first = issues[0]
  if (!first) return undefined
  const diagnostic = insightsDiagnostic({ ...metadata, ...first, phase: 'response_parse' })
  diagnostic.paths.push(`issue_count:${issues.length}`)
  return diagnostic
}

/** Single Insights reporting owner: retain locally, send once, record intake outcome. */
export function reportInsightsFailure(
  shopify: ShopifyTokenSource,
  scopeKey: string,
  record: AttemptRecord,
  diagnostic = insightsDiagnostic({ ...record, failureClass: record.failureClass ?? 'network' }),
) {
  retainInsightsEvidence(scopeKey, { ...record, delivery: 'pending' })
  reportClientDiagnostic(shopify, diagnostic, {
    signal: AbortSignal.timeout(10_000),
    onDelivery: (delivery) => updateInsightsDelivery(scopeKey, record.attemptId, delivery),
  })
}
